import React, { FC } from 'react';
import { toast } from 'react-toastify';

import {
  CopyLinkIcon,
  EditIcon,
  EyeIconSmall,
  ThreeDotsIcon,
  TrashBinIcon,
} from '../../../assets/icons/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/popover/index';
import { SubscriptionOffer } from '../../../types/offer.types';
import { subscriptionTypeTitle } from '../../../types/plan.types';

export type SubscriptionTableItemProps = {
  handleDelete: (offer: SubscriptionOffer) => void;
  handleEdit: (offer: SubscriptionOffer) => void;
  handleView: (offer: SubscriptionOffer) => void;
  offer: SubscriptionOffer;
};

const formatRecipients = (recipients: string[]) => {
  if (recipients.length === 0) return '-';
  const [first, ...rest] = recipients;

  return rest.length > 0 ? `${first} + ${rest.length}` : first;
};

export const SubscriptionTableItem: FC<SubscriptionTableItemProps> = ({
  offer,
  handleView,
  handleEdit,
  handleDelete,
}) => {
  const copyLink = () => {
    void navigator.clipboard
      .writeText(offer.link)
      .then(() => toast.success('Link copied to clipboard'))
      .catch(() => undefined);
  };

  return (
    <tr className="w-full bg-white">
      <td className="max-w-[214px] truncate text-nowrap p-4 text-sm font-[450] text-gray-700">
        {offer.companyName}
      </td>
      <td className="max-w-[214px] truncate p-4 text-sm text-gray-700">{offer.companyDomain}</td>
      <td className="p-4 text-sm text-gray-700">{offer.stakeholdersLimit}</td>
      <td className="max-w-[419px] truncate p-4 text-sm text-gray-700">
        {formatRecipients(offer.recipients)}
      </td>
      <td className="p-4 text-sm text-gray-700">{subscriptionTypeTitle[offer.planType]}</td>
      <td className="w-12 px-4 py-[22px]">
        <Popover>
          <PopoverTrigger className="h-fit w-fit rounded data-[state=open]:shadow-md">
            <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
              <ThreeDotsIcon />
            </div>
          </PopoverTrigger>
          <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
            <PopoverClose>
              <div
                className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => handleView(offer)}
              >
                <EyeIconSmall />
                <span className="text-sm font-normal text-gray-700">View subscription</span>
              </div>

              <div
                className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => handleEdit(offer)}
              >
                <EditIcon />
                <span className="text-sm font-normal text-gray-700">Edit subscription</span>
              </div>

              <div
                className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                onClick={copyLink}
              >
                <CopyLinkIcon />
                <span className="text-sm font-normal text-gray-700">Copy CapQuest Link</span>
              </div>

              <div
                className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-fireside-600 hover:bg-gray-50"
                onClick={() => handleDelete(offer)}
              >
                <TrashBinIcon iconColor="#D92D20" />
                <span className="text-sm font-normal text-fireside-600">Delete</span>
              </div>
            </PopoverClose>
          </PopoverContent>
        </Popover>
      </td>
    </tr>
  );
};
